import { useEffect, useRef, useState, useCallback, type CSSProperties } from 'react';

interface UseInViewOptions {
  /** Fraction of element that must be visible (0–1). Default 0.05 */
  threshold?: number;
  /** Root margin (CSS format). Positive bottom value triggers before element enters viewport. */
  rootMargin?: string;
  /** Only fire once (default true — most scroll animations only play once) */
  once?: boolean;
}

interface UseInViewResult<T extends HTMLElement> {
  ref: (node: T | null) => void;
  inView: boolean;
}

/**
 * Returns a callback ref + a boolean that becomes true when the element
 * enters the viewport. Uses a callback ref (not useRef) so the observer is
 * connected as soon as the DOM node exists. Respects prefers-reduced-motion
 * by returning `true` immediately (skip animation).
 */
/** Max time content may wait for its reveal before we force it visible. */
const REVEAL_FAILSAFE_MS = 1500;

export function useInView<T extends HTMLElement = HTMLDivElement>(
  options: UseInViewOptions = {},
): UseInViewResult<T> {
  const { threshold = 0.05, rootMargin = '0px 0px 100px 0px', once = true } = options;
  const [inView, setInView] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementRef = useRef<T | null>(null);
  const fallbackRef = useRef<number | null>(null);

  // Check reduced motion once
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Callback ref: fires whenever the DOM element is attached or detached
  const ref = useCallback(
    (node: T | null) => {
      // Clean up previous observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      elementRef.current = node;

      // Skip animation for reduced-motion users
      if (prefersReducedMotion) {
        setInView(true);
        return;
      }

      if (!node) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          if (entry.isIntersecting) {
            setInView(true);
            if (once) {
              observerRef.current?.disconnect();
              observerRef.current = null;
            }
          } else if (!once) {
            setInView(false);
          }
        },
        { threshold, rootMargin },
      );

      observerRef.current.observe(node);

      // FAILSAFE (2026-07-10, house rule: the renderer must never hide its
      // own content). Every consumer of this hook gates an opacity-0 reveal
      // state on `inView`; if the observer never fires — any browser quirk,
      // any root/margin misjudgement — that content stays invisible forever.
      // Confirmed live: the match-detail page rendered as blank white cards.
      // A reveal is a bonus, not a gate: force inView after a beat so the
      // worst possible failure is a slightly late animation.
      if (once) {
        if (fallbackRef.current !== null) window.clearTimeout(fallbackRef.current);
        fallbackRef.current = window.setTimeout(() => {
          setInView(true);
          observerRef.current?.disconnect();
          observerRef.current = null;
        }, REVEAL_FAILSAFE_MS);
      }
    },
    [threshold, rootMargin, once, prefersReducedMotion],
  );

  // Cleanup on unmount
  useEffect(() => {
    return (): void => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (fallbackRef.current !== null) {
        window.clearTimeout(fallbackRef.current);
      }
    };
  }, []);

  return { ref, inView };
}

interface UseStaggerChildrenResult<T extends HTMLElement> extends UseInViewResult<T> {
  getStaggerStyle: (index: number) => CSSProperties;
}

/**
 * Hook for staggered children — returns inView + a helper that gives
 * each child a CSS custom property for its stagger delay.
 */
export function useStaggerChildren<T extends HTMLElement = HTMLDivElement>(
  options: UseInViewOptions & { staggerMs?: number } = {},
): UseStaggerChildrenResult<T> {
  const { staggerMs = 60, ...inViewOpts } = options;
  const { ref, inView } = useInView<T>(inViewOpts);

  const getStaggerStyle = useCallback(
    (index: number): CSSProperties =>
      ({
        '--stagger-delay': `${index * staggerMs}ms`,
      }) as CSSProperties,
    [staggerMs],
  );

  return { ref, inView, getStaggerStyle };
}
